import React, { useState } from 'react';
import {
  BsBackpackFill,
  BsBriefcaseFill,
  BsCameraVideoFill,
  BsEnvelopeFill,
  BsFillHouseFill,
  BsList,
  BsMoonStarsFill,
  BsSunFill
} from "react-icons/bs";
import { Container, Box, Menu, MenuItem, IconButton, Button } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import LazyImage from './LazyImage';

const navItems = [
  { to: '/', icon: <BsFillHouseFill />, label: 'Home' },
  { to: '/experience', icon: <BsBriefcaseFill />, label: 'Experience' },
  { to: '/videos', icon: <BsCameraVideoFill />, label: 'Videos' },
  { to: '/projects', icon: <BsBackpackFill />, label: 'Projects' },
  { to: '/contact', icon: <BsEnvelopeFill />, label: 'Contact' },
];

function ThemeToggle({ darkMode, onToggleTheme, mobile = false }) {
  const label = darkMode ? 'Switch to light mode' : 'Switch to dark mode';

  return (
    <Box
      component="button"
      type="button"
      onClick={onToggleTheme}
      aria-label={label}
      aria-pressed={darkMode}
      title={label}
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: '0 0 auto',
        width: mobile ? 58 : 104,
        height: mobile ? 30 : 32,
        px: mobile ? '4px' : '6px',
        border: mobile
          ? `1px solid ${darkMode ? 'rgba(255,255,255,0.46)' : 'rgba(14,43,77,0.5)'}`
          : '1px solid rgba(255,255,255,0.32)',
        borderRadius: 999,
        backgroundColor: darkMode
          ? '#d9e9f7'
          : mobile
            ? '#e7eef5'
            : 'rgba(255,255,255,0.12)',
        color: darkMode ? '#0e2b4d' : '#fff',
        cursor: 'pointer',
        overflow: 'hidden',
        transition: 'background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
        boxShadow: darkMode
          ? '0 0 0 2px rgba(241,196,15,0.18)'
          : mobile
            ? 'inset 0 0 0 1px rgba(14,43,77,0.12)'
            : 'none',
        '&:hover': {
          borderColor: darkMode ? '#f1c40f' : mobile ? '#0e2b4d' : 'rgba(255,255,255,0.55)',
          backgroundColor: darkMode ? '#eef6ff' : mobile ? '#f3f7fb' : 'rgba(255,255,255,0.18)',
        },
        '&:focus-visible': {
          outline: '2px solid #f1c40f',
          outlineOffset: 2,
        },
      }}
    >
      {!mobile && (
        <Box
          component="span"
          sx={{
            pl: darkMode ? 0.6 : 3.7,
            pr: darkMode ? 3.7 : 0.6,
            width: '100%',
            fontSize: '0.74rem',
            fontWeight: 900,
            lineHeight: 1,
            textAlign: darkMode ? 'left' : 'right',
            textTransform: 'none',
            transition: 'padding 0.2s ease',
          }}
        >
          {darkMode ? 'Light' : 'Dark'}
        </Box>
      )}

      <Box
        component="span"
        aria-hidden="true"
        sx={{
          position: 'absolute',
          top: mobile ? 3 : 3,
          left: darkMode ? `calc(100% - ${mobile ? 27 : 27}px)` : mobile ? 3 : 3,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: mobile ? 24 : 24,
          height: mobile ? 24 : 24,
          borderRadius: '50%',
          backgroundColor: darkMode ? '#0e2b4d' : '#f1c40f',
          color: darkMode ? '#f1c40f' : '#0e2b4d',
          fontSize: mobile ? '0.82rem' : '0.8rem',
          boxShadow: '0 3px 10px rgba(0,0,0,0.22)',
          transition: 'left 0.2s ease, background-color 0.2s ease, color 0.2s ease',
        }}
      >
        {darkMode ? <BsSunFill /> : <BsMoonStarsFill />}
      </Box>
    </Box>
  );
}

function Nav({ darkMode, onToggleTheme }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const location = useLocation();
  const open = Boolean(anchorEl);

  const handleToggle = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isActive = (path) => location.pathname === path;
  const currentPageLabel = navItems.find(({ to }) => isActive(to))?.label || 'Home';

  return (
    <Container
      sx={{
        position: 'relative',
        minWidth: '100vw',
        height: '6vh',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#0e2b4d',
        color: '#f7f7f7',
        p: 0,
        m: 0,
        '@media screen and (max-aspect-ratio: 8/10)': {
          height: '6vh',
          px: 1.5,
          alignItems: 'center',
          justifyContent: 'space-between',
          overflowX:'hidden',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.4,
          ml: 2,
          '@media screen and (max-aspect-ratio: 8/10)': {
            width: '100%',
            ml: 0,
            justifyContent: 'space-between',
          },
        }}
      >
        <LazyImage
          src="./assets/logo.png"
          alt="DN logo"
          sx={{
            height: '3.8vh',
            width: 'auto',
          }}
          wrapperSx={{
            height: '3.8vh',
            width: 'auto',
            flex: '0 0 auto',
            '@media screen and (max-aspect-ratio: 8/10)': {
              height: '3.8vh',
              maxWidth: '42vw',
              order: 1,
            },
          }}
        />

        <Box
          sx={{
            display: 'none',
            '@media screen and (max-aspect-ratio: 8/10)': {
              order: 2,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              flex: '0 0 auto',
            },
          }}
        >
          <Box
            component="span"
            sx={{
              color: '#f7f7f7',
              fontWeight: 900,
              fontSize: '0.85rem',
              letterSpacing: 1.1,
              textTransform: 'uppercase',
              lineHeight: 1,
            }}
          >
            {currentPageLabel}
          </Box>

          <IconButton
            aria-controls={open ? 'dropdown-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={handleToggle}
            sx={{
              color: '#0e2b4d',
              fontSize: '2.5vh',
              backgroundColor: '#f7f7f7',
              fontWeight: 'bold',
              borderRadius: 1,
              height: '3vh',
              padding: '0 0.25vh',
              transition: 'transform 0.3s ease-in-out, background-color 0.3s, color 0.3s',
              '&:hover, &:focus, &.Mui-focusVisible': {
                backgroundColor: '#3B806B',
                color: '#f7f7f7',
              },
              transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
              flex: '0 0 auto',
            }}
          >
            <BsList />
          </IconButton>
        </Box>

        <Box
          component="nav"
          aria-label="Primary navigation"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            '@media screen and (max-aspect-ratio: 8/10)': {
              display: 'none',
            },
          }}
        >
          {navItems.map(({ to, icon, label }) => (
            <Button
              key={to}
              component={Link}
              to={to}
              startIcon={icon}
              sx={{
                color: '#f7f7f7',
                borderRadius: 1,
                px: 1.35,
                py: 0.55,
                fontWeight: 800,
                textTransform: 'none',
                bgcolor: isActive(to) ? '#3B806B' : 'transparent',
                border: isActive(to) ? '1px solid rgba(255,255,255,0.22)' : '1px solid transparent',
                '&:hover': {
                  bgcolor: isActive(to) ? '#3B806B' : 'rgba(255,255,255,0.1)',
                  borderColor: 'rgba(255,255,255,0.22)',
                },
              }}
            >
              {label}
            </Button>
          ))}
        </Box>

        <Menu
          id="dropdown-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          PaperProps={{
            sx: {
              border: darkMode ? '1px solid rgba(255,255,255,0.16)' : '1px solid #ccc',
              backgroundColor: darkMode ? '#0e2b4d' : '#fff',
              boxShadow: 4,
              minWidth: { xs: 150, sm: 180 },
              mt: 1,
            },
          }}
          MenuListProps={{
            sx: {
              padding: 0,
              display:'flex',
              flexDirection:'column',
            }
          }}
        >
          {navItems.map(({ to, icon, label }) => (
            <MenuItem
              key={to}
              component={Link}
              to={to}
              onClick={handleClose}
              selected={isActive(to)}
              disableRipple
              disableTouchRipple
              sx={{
                fontWeight: 'bolder',
                color: darkMode ? '#eef6ff' : '#0e2b4d',
                backgroundColor: darkMode ? '#0e2b4d' : '#fff',
                fontSize: '2vh',
                display: 'inline-flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                position: 'relative',
                WebkitTapHighlightColor: 'transparent',
                '&:hover, &:focus, &.Mui-focusVisible': {
                  backgroundColor: darkMode ? 'rgba(255,255,255,0.08) !important' : 'rgba(14,43,77,0.06) !important',
                  color: darkMode ? '#eef6ff !important' : '#0e2b4d !important',
                },
                '&:active': {
                  backgroundColor: darkMode ? 'rgba(255,255,255,0.12) !important' : 'rgba(14,43,77,0.1) !important',
                  color: darkMode ? '#eef6ff !important' : '#0e2b4d !important',
                },
                '&.Mui-selected': {
                  backgroundColor: '#f1c40f !important',
                  color: '#0e2b4d !important',
                },
                '&.Mui-selected:hover, &.Mui-selected:focus, &.Mui-selected.Mui-focusVisible': {
                  backgroundColor: '#f1c40f !important',
                  color: '#0e2b4d !important',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', marginRight: 1 }}>
                {React.cloneElement(icon, { style: { marginRight: 8, position: 'relative', top: '0.3vh' } })}
              </Box>
              {label}
            </MenuItem>
          ))}
          <MenuItem
            component="div"
            disableRipple
            sx={{
              fontWeight: 'bolder',
              color: darkMode ? '#eef6ff' : '#0e2b4d',
              backgroundColor: darkMode ? '#0e2b4d' : '#fff',
              fontSize: '2vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              borderTop: darkMode ? '1px solid rgba(255,255,255,0.22)' : '1px solid rgba(14,43,77,0.16)',
              cursor: 'default',
              WebkitTapHighlightColor: 'transparent',
              '&:hover': {
                backgroundColor: darkMode ? '#0e2b4d !important' : '#fff !important',
              },
            }}
          >
            Theme
            <ThemeToggle darkMode={darkMode} onToggleTheme={onToggleTheme} mobile />
          </MenuItem>
        </Menu>

      </Box>
      <Box
        sx={{
          mr: 2,
          '@media screen and (max-aspect-ratio: 8/10)': {
            display: 'none',
          },
        }}
      >
        <ThemeToggle darkMode={darkMode} onToggleTheme={onToggleTheme} />
      </Box>
    </Container>
  );
}

export default Nav;
